import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  List,
  ListItem,
  Skeleton,
  Alert,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerPointIcon,
  TextSnippet as WordIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Visibility as ViewIcon,
  Schedule as TimeIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SearchResult } from '../../types';
import { formatBytes, formatDistanceToNow } from '../../utils/formatters';

interface SearchResultsProps {
  results: SearchResult[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onResultClick: (result: SearchResult) => void;
  searchTime?: number;
  query: string;
}

const getFileTypeIcon = (mimeType: string) => {
  if (mimeType.includes('pdf')) return <PdfIcon color="error" />;
  if (mimeType.includes('word') || mimeType.includes('docx')) return <WordIcon color="primary" />;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return <ExcelIcon color="success" />;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <PowerPointIcon color="warning" />;
  return <DocumentIcon />;
};


const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isLoading,
  error,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onResultClick,
  searchTime,
  query,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box>
        {Array.from(new Array(5)).map((_, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" gap={2}>
                <Skeleton variant="rectangular" width={48} height={48} />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="40%" height={20} />
                  <Box display="flex" gap={1} mt={1}>
                    <Skeleton variant="rectangular" width={60} height={24} />
                    <Skeleton variant="rectangular" width={80} height={24} />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('search.errorTitle')}
        </Typography>
        {error}
      </Alert>
    );
  }

  if (results.length === 0 && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" gutterBottom color="text.secondary">
          {t('search.noResults')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {query
            ? t('search.noDocumentsMatch', { query })
            : t('search.adjustTermsOrFilters')}
        </Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Box>
      {/* Search Summary */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {query
            ? t('search.resultsSummaryForQuery', { total: totalCount.toLocaleString(), query })
            : t('search.resultsSummary', { total: totalCount.toLocaleString() })}
          {searchTime && ` ${t('search.searchTime', { time: searchTime.toFixed(3) })}`}
        </Typography>

        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('pagination.show')}</InputLabel>
            <Select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              label={t('pagination.show')}
            >
              <MenuItem value={10}>{t('pagination.perPageCount', { count: 10 })}</MenuItem>
              <MenuItem value={20}>{t('pagination.perPageCount', { count: 20 })}</MenuItem>
              <MenuItem value={50}>{t('pagination.perPageCount', { count: 50 })}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Results List */}
      <List disablePadding>
        {results.map((result) => (
          <ListItem key={result.id} disablePadding sx={{ mb: 1 }}>
            <Card
              sx={{
                width: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => onResultClick(result)}
            >
              <CardContent>
                <Box display="flex" gap={2}>
                  {/* File Icon */}
                  <Box display="flex" alignItems="flex-start" pt={0.5}>
                    {getFileTypeIcon(result.mimeType || 'application/octet-stream')}
                  </Box>

                  {/* Content */}
                  <Box flex={1} minWidth={0}>
                    {/* Title and Score */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 'medium',
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: result.highlights?.title || result.title
                        }}
                      />
                      {result.score && (
                        <Chip
                          label={`${Math.round(result.score * 100)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Description/Snippet */}
                    {(result.highlights?.content || result.description) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.4,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: result.highlights?.content || result.description || ''
                        }}
                      />
                    )}

                    {/* Metadata */}
                    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
                      {result.category && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CategoryIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {result.category}
                          </Typography>
                        </Box>
                      )}

                      {result.uploadedBy && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {result.uploadedBy}
                          </Typography>
                        </Box>
                      )}

                      {result.uploadedAt && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {t('time.ago', { value: formatDistanceToNow(new Date(result.uploadedAt)) })}
                          </Typography>
                        </Box>
                      )}

                      {result.fileSize && (
                        <Typography variant="caption" color="text.secondary">
                          {formatBytes(result.fileSize)}
                        </Typography>
                      )}
                    </Box>

                    {/* Tags */}
                    {result.tags && result.tags.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                        {result.tags.slice(0, 5).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        ))}
                        {result.tags.length > 5 && (
                          <Chip
                            label={t('common.moreCount', { count: result.tags.length - 5 })}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ height: 20 }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Actions */}
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onResultClick(result);
                        }}
                      >
                        {t('common.view')}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle download
                        }}
                      >
                        <Tooltip title={t('common.download')}>
                          <DownloadIcon fontSize="small" />
                        </Tooltip>
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle share
                        }}
                      >
                        <Tooltip title={t('common.share')}>
                          <ShareIcon fontSize="small" />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </ListItem>
        ))}
      </List>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_event, newPage) => onPageChange(newPage)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchResults;
